/**
 * Authentication Context for Project Minerva
 * 
 * Provides authentication state and methods throughout the application.
 * Supports email/password and Google OAuth authentication.
 * Implements role-based access control with 'investor' and 'founder' roles.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import axios from 'axios';

export type UserRole = 'investor' | 'founder' | 'admin';

// In local dev, empty so Vite proxy handles /api/*. In production, set to backend URL at build time.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface AuthUser extends User {
  role?: UserRole;
  full_name?: string;
  job_title?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (email: string, password: string, role: UserRole, displayName?: string) => Promise<void>;
  completeGoogleRegistration: (email: string, role: UserRole, displayName?: string) => Promise<void>;
  signOut: () => Promise<void>;
  isInvestor: boolean;
  isFounder: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch user info from backend
  const fetchUserInfo = async (firebaseUser: User): Promise<Partial<AuthUser>> => {
    try {
      const idToken = await firebaseUser.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/api/firebase-auth/me`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      return {
        role: response.data.role,
        full_name: response.data.full_name,
        job_title: response.data.job_title
      };
    } catch (err) {
      console.error('Failed to fetch user info:', err);
      return {};
    }
  };

  // Set up auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Fetch role and info from backend

        const userInfo = await fetchUserInfo(firebaseUser);
        const authUser = firebaseUser as AuthUser;
        Object.assign(authUser, userInfo);
        setUser(authUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userInfo = await fetchUserInfo(userCredential.user);
      const authUser = userCredential.user as AuthUser;
      Object.assign(authUser, userInfo);
      setUser(authUser);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);
      const userCredential = await signInWithPopup(auth, googleProvider);

      // Check if user has a role, if not, they need to complete registration
      const userInfo = await fetchUserInfo(userCredential.user);

      if (!userInfo.role) {
        // New Google user - needs role assignment
        // Keep them signed in so they can complete registration
        // Store user without role temporarily
        const authUser = userCredential.user as AuthUser;
        // Don't assign other userInfo if role is missing, just keep it raw? 
        // Or we set authUser.role = undefined explicitely if needed, but it is undefined by default on User.
        // The original code did spread userCredential.user. 
        // Let's just cast it.
        setUser(authUser);

        const error = new Error('UNREGISTERED_USER');
        error.name = 'UnregisteredUserError';
        throw error;
      }

      const authUser = userCredential.user as AuthUser;
      Object.assign(authUser, userInfo);
      setUser(authUser);
    } catch (err: any) {
      setError(err.message);
      // Sign out only for actual errors (not for unregistered users)
      if (err.name !== 'UnregisteredUserError') {
        await firebaseSignOut(auth);
        setUser(null);
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign up with email and password
  const signUp = async (email: string, password: string, role: UserRole, displayName?: string) => {
    try {
      setError(null);
      setLoading(true);

      const response = await axios.post(`${API_BASE_URL}/api/firebase-auth/register`, {
        email,
        password,
        role,
        display_name: displayName
      });

      if (response.data.success) {
        // Sign in the newly created user (only if not existing user)
        if (!response.data.existing_user) {
          await signIn(email, password);
        } else {
          // For existing users (Google sign-in), just fetch the role
          const currentUser = auth.currentUser;
          if (currentUser) {
            const userInfo = await fetchUserInfo(currentUser);

            const authUser = currentUser as AuthUser;
            Object.assign(authUser, userInfo);
            setUser(authUser);
          }
        }
      } else {
        throw new Error('Failed to create user');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Complete registration for Google sign-in users
  const completeGoogleRegistration = async (email: string, role: UserRole, displayName?: string) => {
    try {
      setError(null);
      setLoading(true);

      // Get current Firebase user (should be signed in via Google)
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user found. Please sign in with Google again.');
      }

      // Get ID token for authentication
      const idToken = await currentUser.getIdToken();

      // Complete registration via backend API (set role for existing user)
      const response = await axios.post(`${API_BASE_URL}/api/firebase-auth/register`,
        {
          email,
          password: 'dummy-password-not-used', // Not used for existing users
          role,
          display_name: displayName
        },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        }
      );

      if (response.data.success) {
        // Fetch updated user with role
        const userInfo = await fetchUserInfo(currentUser);

        const authUser = currentUser as AuthUser;
        Object.assign(authUser, userInfo);
        setUser(authUser);
      } else {
        throw new Error('Failed to complete registration');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.error || err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
      setUser(null);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    signIn,
    signInWithGoogle,
    signUp,
    completeGoogleRegistration,
    signOut,
    isInvestor: user?.role === 'investor',
    isFounder: user?.role === 'founder',
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
